/**
 * Question Selector — v11.2 FamilyId-First Selection Algorithm
 *
 * The core question selection function that picks a single question
 * using strict pedagogical rules and group/tier gating.
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 * v11.3: Skill interleaving (P-11) + cognitive diversity (P-10).
 */

import {
  type ModuleId,
  type Question,
  getQuestionWithExplanation,
  getFamiliesForModule,
  type FamilyDefinition,
  QUESTIONS_BY_FAMILY,
} from '../../content'
// Deep import (NOT the governance barrel) — keeps the mastery hot-path from
// pulling the entire governance pipeline into the bundle eagerly. See
// selectionConfig.ts comment for the boot-perf rationale.
import { getLDG } from '../../governance/ldg'
import { GroupId } from '../../config/systemConfig'
import { FamilyMasteryState, getFamilyMasteryKey } from '../familyMasteryEngine'
import { computeDecayedMastery } from '../masteryDecay'
import { warn as logWarn } from '../../utils/logger'
import type { SeededRng } from '../../utils/deterministicRng'
import { V11_CONFIG, debugLog } from './selectionConfig'
import {
  computeFamilyPriority,
  computeMasteryConfidence,
  isMasteryStable,
  determineTargetTier,
} from './familyScoring'
import { buildTierWindow, findQuestionsForFamily } from './tierWindow'
import { arePrerequisitesMet } from './prerequisiteCheck'
import type { InferredMasteryEntry } from '../inferredMastery'
import { detectCognitiveLevel } from '../../governance/cognitiveLevel'

// ============================================================
// PRIVATE HELPERS — DRY extraction (H-1 audit)
// ============================================================

interface QuestionSearchOpts {
  /** Apply soft preference for non-MC types after consecutive MC */
  applyInteractivePref?: boolean
  sessionTypeHistory?: string[]
  /** Allow recent questions in final all-tiers fallback (emergency only) */
  allowRecentInFinalFallback?: boolean
  sessionCognitiveLevels?: string[]
}

/**
 * Find a question for a given family using progressive tier window expansion.
 *
 * Search order:
 * 1. Normal tier window (±1) → exclude recent
 * 2. Expanded tier window (±FALLBACK) → exclude recent
 * 3. All allowed tiers → exclude recent (unless allowRecentInFinalFallback)
 *
 * Returns the candidate list (caller picks via rng) or empty array.
 *
 * Extracted in H-1 audit to eliminate 4× duplication of this pattern.
 */
function findCandidatesWithFallback(
  familyId: string,
  targetTier: number,
  currentMastery: number,
  allowedTiers: Set<number>,
  sessionQuestionIds: Set<string>,
  recentQuestionIds: Set<string>,
  opts?: QuestionSearchOpts,
): { candidates: Question[]; fallbackReason: string } {
  let candidates: Question[]
  let fallbackReason = ''

  // Step 1: Normal tier window
  const tierWindow = buildTierWindow(
    targetTier,
    V11_CONFIG.TIER_WINDOW_BELOW,
    V11_CONFIG.TIER_WINDOW_ABOVE,
    allowedTiers,
    currentMastery,
  )
  candidates = findQuestionsForFamily(
    familyId,
    tierWindow,
    sessionQuestionIds,
  ) as Question[]

  // Optional: soft preference for interactive types
  if (
    opts?.applyInteractivePref &&
    candidates.length > 1 &&
    opts.sessionTypeHistory &&
    opts.sessionTypeHistory.length >= V11_CONFIG.MAX_CONSECUTIVE_MC
  ) {
    const recentTypes = opts.sessionTypeHistory.slice(
      -V11_CONFIG.MAX_CONSECUTIVE_MC,
    )
    if (recentTypes.every((t) => t === 'multiple_choice')) {
      const interactiveCandidates = candidates.filter(
        (q) => q.questionType && q.questionType !== 'multiple_choice',
      )
      if (interactiveCandidates.length > 0) {
        candidates = interactiveCandidates
        debugLog(
          `   🎲 Soft preference applied: filtered to ${interactiveCandidates.length} interactive questions after ${V11_CONFIG.MAX_CONSECUTIVE_MC} consecutive MC`,
        )
      }
    }
  }

  // Exclude recent questions
  const nonRecent = candidates.filter((q) => !recentQuestionIds.has(q.id))
  if (nonRecent.length > 0) {
    const excludedCount = candidates.length - nonRecent.length
    candidates = nonRecent
    if (excludedCount > 0) {
      debugLog(`   🕐 Excluded ${excludedCount} recent questions`)
    }
  }

  if (candidates.length > 0) return { candidates, fallbackReason }

  // Step 2: Expanded tier window
  fallbackReason = `expanded window (±${V11_CONFIG.TIER_WINDOW_FALLBACK})`
  const expandedWindow = buildTierWindow(
    targetTier,
    V11_CONFIG.TIER_WINDOW_FALLBACK,
    V11_CONFIG.TIER_WINDOW_FALLBACK,
    allowedTiers,
    currentMastery,
  )
  candidates = findQuestionsForFamily(
    familyId,
    expandedWindow,
    sessionQuestionIds,
  ) as Question[]
  const nonRecentExpanded = candidates.filter(
    (q) => !recentQuestionIds.has(q.id),
  )
  if (nonRecentExpanded.length > 0) candidates = nonRecentExpanded

  if (candidates.length > 0) return { candidates, fallbackReason }

  // Step 3: All allowed tiers
  fallbackReason = 'any allowed tier'
  candidates = findQuestionsForFamily(
    familyId,
    allowedTiers,
    sessionQuestionIds,
  ) as Question[]
  if (!opts?.allowRecentInFinalFallback) {
    const nonRecentAll = candidates.filter((q) => !recentQuestionIds.has(q.id))
    if (nonRecentAll.length > 0) candidates = nonRecentAll
  }

  return { candidates, fallbackReason }
}

/**
 * Score eligible families: look up group-scoped mastery, compute priority,
 * and sort by priority descending.
 *
 * v11.3: Accepts recentSkills for interleaving penalty (P-11).
 *
 * Extracted in H-1 audit to eliminate 4× duplication of the scoring pattern.
 */
function scoreFamilies(
  families: FamilyDefinition[],
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  recentFamilyIds: Set<string>,
  periodTier: number | undefined,
  opts?: { applyDecay?: boolean; now?: number; recentSkills?: string[] },
): {
  family: FamilyDefinition
  mastery: FamilyMasteryState | undefined
  priority: number
  confidence: number
}[] {
  return families
    .map((family) => {
      const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup)
      const masteryState = familyMastery[masteryKey]
      let effectiveState = masteryState
      if (opts?.applyDecay && masteryState) {
        const decayedScore = computeDecayedMastery(masteryState, opts.now)
        effectiveState = { ...masteryState, masteryScore: decayedScore }
      }
      return {
        family,
        mastery: effectiveState,
        priority: computeFamilyPriority(
          family,
          effectiveState,
          recentFamilyIds,
          periodTier,
          opts?.recentSkills,
        ),
        confidence: computeMasteryConfidence(effectiveState),
      }
    })
    .sort((a, b) => b.priority - a.priority)
}

/**
 * Pick a question from a scored family pool: select top candidate family,
 * then find a question using progressive tier window expansion.
 *
 * v11.3: Applies cognitive diversity soft preference (P-10).
 *
 * Returns null if no question found from any family in the pool.
 */
function pickFromScoredPool(
  scoredFamilies: {
    family: FamilyDefinition
    mastery: FamilyMasteryState | undefined
    priority: number
    confidence: number
  }[],
  allowedTiers: Set<number>,
  sessionQuestionIds: Set<string>,
  recentQuestionIds: Set<string>,
  rng: SeededRng,
  searchOpts?: QuestionSearchOpts,
): { question: Question | null; reason: string; family: FamilyDefinition } {
  const poolSize = Math.min(
    V11_CONFIG.TOP_CANDIDATE_POOL,
    scoredFamilies.length,
  )
  const topCandidates = scoredFamilies.slice(0, poolSize)
  const selected = rng.pick(topCandidates)!
  const { family, mastery: masteryState } = selected

  const targetTier = determineTargetTier(family, masteryState, allowedTiers)
  const currentMastery = masteryState?.masteryScore ?? 0

  const { candidates, fallbackReason } = findCandidatesWithFallback(
    family.familyId,
    targetTier,
    currentMastery,
    allowedTiers,
    sessionQuestionIds,
    recentQuestionIds,
    searchOpts,
  )

  if (candidates.length > 0) {
    // v11.3: Cognitive diversity soft preference (P-10)
    // If recent questions were all the same cognitive level, prefer a different level.
    let finalCandidates = candidates
    if (
      searchOpts?.sessionCognitiveLevels &&
      searchOpts.sessionCognitiveLevels.length >=
        V11_CONFIG.COGNITIVE_DIVERSITY_WINDOW &&
      candidates.length > 1
    ) {
      const recentLevels = searchOpts.sessionCognitiveLevels.slice(
        -V11_CONFIG.COGNITIVE_DIVERSITY_WINDOW,
      )
      const allSame = recentLevels.every((l) => l === recentLevels[0])
      if (allSame) {
        const dominantLevel = recentLevels[0]
        const diverse = candidates.filter(
          (q) => detectCognitiveLevel(q) !== dominantLevel,
        )
        if (diverse.length > 0) {
          finalCandidates = diverse
          debugLog(
            `   🧠 Cognitive diversity: filtered ${candidates.length} → ${diverse.length} candidates (avoiding "${dominantLevel}")`,
          )
        }
      }
    }

    const shuffled = rng.shuffle(finalCandidates)
    const picked = shuffled[0]
    return {
      question: getQuestionWithExplanation(picked.id),
      reason:
        fallbackReason ||
        `direct match (family: ${family.familyId}, targetTier: ${targetTier})`,
      family,
    }
  }

  return { question: null, reason: '', family }
}

// ============================================================
// PUBLIC API
// ============================================================

/**
 * Select a single question using the v11.2 familyId-first algorithm
 * with strict pedagogical rules and group/tier gating.
 *
 * v11.2 Enhancements:
 * - Per-family question cap (max 2 per family)
 * - Recency-based question exclusion
 * - Mastery confidence checks
 * - Controlled reinforcement mode
 * - Soft preference for interactive types
 * v11.8: B/M/E period tier for priority boost (QF-26 Optie B)
 * v11.3: Skill interleaving (P-11) + cognitive diversity (P-10)
 *
 * GROUP-SCOPED MASTERY: This function now uses group-scoped mastery keys internally.
 *
 * @param now - Optional timestamp for deterministic testing (H-5 audit)
 * @param recentSkills - Recent session skill names for interleaving (P-11)
 * @param sessionCognitiveLevels - Recent cognitive levels for diversity (P-10)
 */
export function selectAdaptiveQuestionV11(
  moduleId: ModuleId,
  familyMastery: Record<string, FamilyMasteryState>,
  selectedGroup: GroupId,
  allowedTiers: Set<number>,
  sessionFamilyIds: Set<string>,
  sessionQuestionIds: Set<string>,
  recentFamilyIds: Set<string>,
  recentQuestionIds: Set<string>,
  sessionTypeHistory: string[],
  masteredCount: number,
  familyQuestionCounts: Map<string, number>,
  sessionSize: number,
  rng: SeededRng,
  effectiveMaxPerFamily: number = V11_CONFIG.MAX_QUESTIONS_PER_FAMILY,
  periodTier?: number,
  now?: number,
  recentSkills?: string[],
  sessionCognitiveLevels?: string[],
  /**
   * FG3 (S6) — optional inferred mastery layer. When supplied, cold-start
   * prereqs are treated as soft-pending (no hard block) per the inferred
   * model in `prerequisiteCheck.ts`. Omit to preserve pre-S6 behaviour.
   */
  inferredMastery?: Record<string, InferredMasteryEntry>,
): {
  question: Question | null
  reason: string
  fallbackLevel: number
} {
  // 1. Get all families for this module from taxonomy
  const allModuleFamilies = getFamiliesForModule(moduleId)
  if (allModuleFamilies.length === 0) {
    const reason = `No families found in taxonomy for module ${moduleId}`
    debugLog(`⚠️ v11.2: ${reason}`)
    return { question: null, reason, fallbackLevel: 0 }
  }

  // 2. CRITICAL: Filter families to only those whose tier is within allowed tiers
  const eligibleFamilies = allModuleFamilies.filter((f) =>
    allowedTiers.has(f.tier),
  )

  // 2b. NEW: Filter by LDG prerequisites (CONFLICT-01 resolution)
  const prerequisiteReadyFamilies = eligibleFamilies.filter((f) =>
    arePrerequisitesMet(
      f.familyId,
      familyMastery,
      selectedGroup,
      V11_CONFIG.LDG_PREREQUISITE_MASTERY,
      inferredMastery,
    ),
  )

  // v11.4: When no families pass prerequisite check, fall back to ROOT families only
  let selectionPool: FamilyDefinition[]
  if (prerequisiteReadyFamilies.length > 0) {
    selectionPool = prerequisiteReadyFamilies
  } else {
    const graph = getLDG()
    const rootOnlyFamilies = eligibleFamilies.filter((f) => {
      const node = graph.nodes.get(f.familyId)
      return !node || node.prerequisites.length === 0
    })

    if (rootOnlyFamilies.length > 0) {
      selectionPool = rootOnlyFamilies
      debugLog(
        `🔒 LDG Gating: No prerequisite-ready families. Falling back to ${rootOnlyFamilies.length} root families (no prerequisites).`,
      )
    } else {
      selectionPool = eligibleFamilies
      debugLog(
        `⚠️ LDG Gating: No root families available either. Emergency fallback to all ${eligibleFamilies.length} eligible families.`,
      )
    }
  }

  if (prerequisiteReadyFamilies.length < eligibleFamilies.length) {
    debugLog(
      `🔒 LDG Gating: ${eligibleFamilies.length - prerequisiteReadyFamilies.length} families locked by prerequisites (${prerequisiteReadyFamilies.length}/${eligibleFamilies.length} ready, threshold: ${V11_CONFIG.LDG_PREREQUISITE_MASTERY})`,
    )
  }

  if (selectionPool.length === 0) {
    const reason = `No families for module ${moduleId} within allowed tiers [${Array.from(allowedTiers).join(',')}] for group ${selectedGroup}`
    debugLog(`⚠️ v11.2: ${reason}`)
    return { question: null, reason, fallbackLevel: 0 }
  }
  debugLog(
    `📋 v11.2: Module ${moduleId}, Group ${selectedGroup}, Allowed tiers: [${Array.from(
      allowedTiers,
    )
      .sort((a, b) => a - b)
      .join(
        ',',
      )}], Eligible families: ${selectionPool.length}/${allModuleFamilies.length} (${prerequisiteReadyFamilies.length} prereq-ready)`,
  )

  // v11.2: Calculate mastered cap
  const maxMasteredAllowed = Math.min(
    V11_CONFIG.MAX_MASTERED_PER_SESSION,
    Math.ceil(sessionSize * V11_CONFIG.MAX_MASTERED_PERCENTAGE),
  )

  // ── Shared filter: per-family cap ──
  const underCapFamilies = (families: FamilyDefinition[]) =>
    families.filter((f) => {
      const usageCount = familyQuestionCounts.get(f.familyId) || 0
      if (usageCount >= effectiveMaxPerFamily) {
        debugLog(
          `   ⚠️ Family ${f.familyId} hit per-family cap (${usageCount}/${effectiveMaxPerFamily})`,
        )
        return false
      }
      return true
    })

  // ── Shared filter: mastered cap ──
  const applyMasteredCap = (
    scored: {
      family: FamilyDefinition
      mastery: FamilyMasteryState | undefined
      priority: number
      confidence: number
    }[],
  ) =>
    scored.filter((sf) => {
      if (masteredCount >= maxMasteredAllowed) {
        const mastery = sf.mastery?.masteryScore ?? 0
        const isStable = isMasteryStable(sf.mastery)
        if (mastery >= V11_CONFIG.MASTERED_THRESHOLD && isStable) return false
      }
      return true
    })

  // 3. Score and sort families by priority (main path: new families only, with decay)
  const scoredFamilies = applyMasteredCap(
    scoreFamilies(
      underCapFamilies(selectionPool).filter(
        (f) => !sessionFamilyIds.has(f.familyId),
      ),
      familyMastery,
      selectedGroup,
      recentFamilyIds,
      periodTier,
      { applyDecay: true, now, recentSkills },
    ),
  )

  debugLog(`🔍 v11.2 Filter Chain:
    - Total eligible families: ${eligibleFamilies.length}
    - After per-family cap: ${underCapFamilies(eligibleFamilies).length}
    - After session exclusion: ${underCapFamilies(eligibleFamilies).filter((f) => !sessionFamilyIds.has(f.familyId)).length}
    - After mastered cap (${masteredCount}/${maxMasteredAllowed}): ${scoredFamilies.length}
    - Mastered families in pool: ${scoredFamilies.filter((sf) => (sf.mastery?.masteryScore ?? 0) >= V11_CONFIG.MASTERED_THRESHOLD).length}`)

  if (scoredFamilies.length === 0) {
    // ── FALLBACK LEVEL 1: Reuse families (respect per-family cap + mastered cap) ──
    debugLog(
      `⚠️ v11.2: No new families available. FALLBACK LEVEL 1: Reusing families (respecting per-family cap)...`,
    )
    const reusable = applyMasteredCap(
      scoreFamilies(
        underCapFamilies(eligibleFamilies),
        familyMastery,
        selectedGroup,
        recentFamilyIds,
        periodTier,
        { now },
      ),
    )

    if (reusable.length > 0) {
      debugLog(
        `✅ v11.2: Found ${reusable.length} reusable families (FALLBACK LEVEL 1)`,
      )
      const result = pickFromScoredPool(
        reusable,
        allowedTiers,
        sessionQuestionIds,
        recentQuestionIds,
        rng,
        { applyInteractivePref: true, sessionTypeHistory },
      )
      if (result.question) {
        debugLog(
          `✅ v11.2: Picked question ${result.question.id} (family: ${result.family.familyId}) - FALLBACK LEVEL 1`,
        )
        return {
          question: result.question,
          reason: 'family reuse (respecting per-family cap)',
          fallbackLevel: 1,
        }
      }
    }

    // ── FALLBACK LEVEL 2: Controlled reinforcement (mastered families) ──
    debugLog(
      `⚠️ v11.2: FALLBACK LEVEL 2: Controlled reinforcement (mastered families)...`,
    )
    const mastered = scoreFamilies(
      underCapFamilies(eligibleFamilies),
      familyMastery,
      selectedGroup,
      recentFamilyIds,
      periodTier,
      { now },
    ).filter((sf) => {
      const mastery = sf.mastery?.masteryScore ?? 0
      return (
        mastery >= V11_CONFIG.MASTERED_THRESHOLD && isMasteryStable(sf.mastery)
      )
    })

    if (mastered.length > 0) {
      debugLog(
        `✅ v11.2: Found ${mastered.length} mastered families for reinforcement (FALLBACK LEVEL 2)`,
      )
      const result = pickFromScoredPool(
        mastered,
        allowedTiers,
        sessionQuestionIds,
        recentQuestionIds,
        rng,
      )
      if (result.question) {
        debugLog(
          `✅ v11.2: Picked reinforcement question ${result.question.id} (family: ${result.family.familyId})`,
        )
        return {
          question: result.question,
          reason: 'reinforcement (controlled, stable mastery)',
          fallbackLevel: 2,
        }
      }
    }

    // ── FALLBACK LEVEL 3: Emergency (any family, ignore all caps) ──
    logWarn(
      `🚨 LDG Emergency Fallback triggered: module=${moduleId}, group=${selectedGroup}, ` +
        `sessionQuestions=${sessionQuestionIds.size}, allowedTiers=[${Array.from(allowedTiers).join(',')}]. ` +
        `This means the normal LDG selection path found no suitable questions.`,
    )
    debugLog(`⚠️ v11.2: FALLBACK LEVEL 3: Emergency fallback (ignore caps)...`)
    const emergency = scoreFamilies(
      eligibleFamilies,
      familyMastery,
      selectedGroup,
      recentFamilyIds,
      periodTier,
      { now },
    )

    if (emergency.length > 0) {
      debugLog(
        `✅ v11.2: Found ${emergency.length} families (FALLBACK LEVEL 3 - emergency)`,
      )
      const result = pickFromScoredPool(
        emergency,
        allowedTiers,
        sessionQuestionIds,
        recentQuestionIds,
        rng,
        { allowRecentInFinalFallback: true },
      )
      if (result.question) {
        debugLog(
          `✅ v11.2: Picked emergency question ${result.question.id} (family: ${result.family.familyId})`,
        )
        return {
          question: result.question,
          reason: 'emergency fallback (all caps ignored)',
          fallbackLevel: 3,
        }
      }
    }

    const reason = `No questions available for ${moduleId} in group ${selectedGroup} (allowed tiers: [${Array.from(allowedTiers).join(',')}]) after all fallbacks`
    debugLog(`❌ v11.2: ${reason}`)
    return { question: null, reason, fallbackLevel: 4 }
  }

  // ── MAIN PATH: Pick from top candidates ──
  const result = pickFromScoredPool(
    scoredFamilies,
    allowedTiers,
    sessionQuestionIds,
    recentQuestionIds,
    rng,
    { applyInteractivePref: true, sessionTypeHistory, sessionCognitiveLevels },
  )

  if (result.question) {
    debugLog(
      `✅ v11.2: Picked ${result.question.id} (family: ${result.family.familyId}) — ${result.reason}`,
    )
    return {
      question: result.question,
      reason: result.reason,
      fallbackLevel: 0,
    }
  }

  // Main path family had no questions — try adjacent families (same skill)
  const { family } = result
  debugLog(
    `⚠️ v11.2: No questions for ${family.familyId}. Trying adjacent families...`,
  )
  const adjacentFamilies = eligibleFamilies.filter(
    (f) =>
      f.familyId !== family.familyId &&
      f.skill === family.skill &&
      (familyQuestionCounts.get(f.familyId) || 0) < effectiveMaxPerFamily,
  )
  for (const adjFamily of adjacentFamilies) {
    const adjMasteryKey = getFamilyMasteryKey(adjFamily.familyId, selectedGroup)
    const adjMasteryState = familyMastery[adjMasteryKey]
    const adjMastery = adjMasteryState
      ? computeDecayedMastery(adjMasteryState, now)
      : 0
    const adjTargetTier = determineTargetTier(
      adjFamily,
      adjMasteryState,
      allowedTiers,
    )

    const { candidates } = findCandidatesWithFallback(
      adjFamily.familyId,
      adjTargetTier,
      adjMastery,
      allowedTiers,
      sessionQuestionIds,
      recentQuestionIds,
    )
    if (candidates.length > 0) {
      const shuffled = rng.shuffle(candidates)
      const picked = shuffled[0]
      debugLog(
        `✅ v11.2: Found question via adjacent family ${adjFamily.familyId}`,
      )
      return {
        question: getQuestionWithExplanation(picked.id),
        reason: `adjacent family fallback (${adjFamily.familyId})`,
        fallbackLevel: 0,
      }
    }
  }

  // Final fallback: try ANY eligible family at ANY allowed tier
  debugLog(
    `⚠️ v11.2: Adjacent families exhausted. Trying any eligible family...`,
  )
  for (const f of underCapFamilies(eligibleFamilies)) {
    const { candidates } = findCandidatesWithFallback(
      f.familyId,
      f.tier,
      0,
      allowedTiers,
      sessionQuestionIds,
      recentQuestionIds,
    )
    if (candidates.length > 0) {
      const shuffled = rng.shuffle(candidates)
      const picked = shuffled[0]
      debugLog(`✅ v11.2: Found question via fallback family ${f.familyId}`)
      return {
        question: getQuestionWithExplanation(picked.id),
        reason: `any eligible family fallback (${f.familyId})`,
        fallbackLevel: 0,
      }
    }
  }

  const reason = `No questions available for ${moduleId} in group ${selectedGroup} (allowed tiers: [${Array.from(allowedTiers).join(',')}]) after all fallbacks`
  debugLog(`❌ v11.2: ${reason}`)
  return { question: null, reason, fallbackLevel: 4 }
}
